"use client";

import { useEffect, useRef } from "react";
import { CheckCircle, XCircle, Info, X } from "lucide-react";
import { useToastStore, type Toast, type ToastType } from "@/stores/toastStore";

const TOAST_DURATION = 3000;

const typeStyles: Record<ToastType, { border: string; icon: React.ReactNode }> =
  {
    success: {
      border: "border-l-4 border-success",
      icon: <CheckCircle className="w-5 h-5 text-success shrink-0" />,
    },
    error: {
      border: "border-l-4 border-danger",
      icon: <XCircle className="w-5 h-5 text-danger shrink-0" />,
    },
    info: {
      border: "border-l-4 border-primary",
      icon: <Info className="w-5 h-5 text-primary shrink-0" />,
    },
  };

function ToastItem({ toast }: { toast: Toast }) {
  const remove = useToastStore((s) => s.remove);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(TOAST_DURATION);
  const startedRef = useRef(Date.now());

  const { border, icon } = typeStyles[toast.type];

  useEffect(() => {
    startedRef.current = Date.now();
    timerRef.current = setTimeout(() => remove(toast.id), TOAST_DURATION);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast.id, remove]);

  function handleMouseEnter() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      remainingRef.current -= Date.now() - startedRef.current;
    }
  }

  function handleMouseLeave() {
    startedRef.current = Date.now();
    timerRef.current = setTimeout(() => remove(toast.id), remainingRef.current);
  }

  return (
    <div
      role="status"
      aria-live="polite"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg bg-surface border border-border min-w-72 max-w-sm ${border}`}
    >
      {icon}
      <p className="flex-1 text-sm text-text">{toast.message}</p>
      <button
        type="button"
        aria-label="닫기"
        onClick={() => remove(toast.id)}
        className="min-h-11 min-w-11 flex items-center justify-center rounded text-text-subtle hover:text-text transition"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);

  if (toasts.length === 0) return null;

  return (
    <div
      className={[
        "fixed z-50 flex gap-2",
        // 모바일: 하단 중앙, 새 항목이 위로 쌓이도록 flex-col-reverse
        "flex-col-reverse bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)]",
        // 데스크탑: 우상단, 새 항목이 아래로 쌓이도록 flex-col
        "sm:flex-col sm:bottom-auto sm:top-4 sm:left-auto sm:right-4 sm:translate-x-0 sm:w-auto",
      ].join(" ")}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}
